type Particle = {x:number;y:number;vx:number;vy:number;life:number;max:number};
export class Cosmos {
 private ctx: CanvasRenderingContext2D;
 private width = 0;
 private height = 0;
 private particles: Particle[] = [];
 private rings: {x:number;y:number;age:number;big:boolean}[] = [];
 private celebrations: {x:number;y:number;age:number;pattern:number;rotation:number}[] = [];
 private lastPattern = -1;
 private last = 0;
 private reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
 constructor(private canvas: HTMLCanvasElement) {
  this.ctx = canvas.getContext('2d')!;
  new ResizeObserver(()=>this.resize()).observe(canvas);
  requestAnimationFrame(t=>this.draw(t));
 }
 private resize() {
  const rect = this.canvas.getBoundingClientRect(); this.width=rect.width;this.height=rect.height;
  const dpr=Math.min(devicePixelRatio,2);this.canvas.width=rect.width*dpr;this.canvas.height=rect.height*dpr;this.ctx.setTransform(dpr,0,0,dpr,0,0);
 }
 burst(x:number,y:number,big=false) {
  this.rings.push({x,y,age:0,big});
  if(this.reduced)return;
  for(let i=0;i<(big?65:24);i++){const angle=Math.random()*Math.PI*2,speed=30+Math.random()*100;this.particles.push({x,y,vx:Math.cos(angle)*speed,vy:Math.sin(angle)*speed,life:1.4,max:1.4});}
 }
 celebrate(x:number,y:number) {
  // Pick a different pattern each time, without tying it to scripture order.
  const pattern=(this.lastPattern+1+Math.floor(Math.random()*4))%5;
  this.lastPattern=pattern;
  if(this.reduced){this.burst(x,y,true);return;}
  this.celebrations.push({x,y,age:0,pattern,rotation:Math.random()*Math.PI*2});
  for(let i=0;i<64;i++) {
   const angle=i/64*Math.PI*2, speed=pattern===2?60+Math.random()*150:55+Math.random()*90;
   this.particles.push({x,y,vx:Math.cos(angle)*speed,vy:Math.sin(angle)*speed,life:1.8,max:1.8});
  }
 }
 private drawCelebrations(dt:number) {
  const c=this.ctx;
  this.celebrations=this.celebrations.filter(e=>e.age<1.8);
  for(const e of this.celebrations) {
   e.age+=dt;
   const progress=Math.min(e.age/1.8,1),radius=25+180*(1-Math.pow(1-progress,3));
   c.save();c.translate(e.x,e.y);c.rotate(e.rotation+progress*.6);
   c.globalAlpha=Math.sin(Math.PI*progress)*.65;
   c.strokeStyle=e.pattern%2 || e.pattern===4?'#dec58b':'#76e8d4';c.lineWidth=1.2;
   if(e.pattern===0) {
    // Three concentric ripples.
    for(let i=0;i<3;i++){c.beginPath();c.arc(0,0,Math.max(1,radius-i*28),0,Math.PI*2);c.stroke();}
   } else if(e.pattern===1) {
    // An opening twelve-petal lotus.
    for(let i=0;i<12;i++){c.save();c.rotate(i*Math.PI/6);c.beginPath();c.ellipse(0,-radius*.45,radius*.18,radius*.45,0,0,Math.PI*2);c.stroke();c.restore();}
   } else if(e.pattern===2) {
    // Six spiraling trails.
    for(let arm=0;arm<6;arm++){c.beginPath();for(let i=0;i<=50;i++){const r=radius*i/50,a=arm*Math.PI/3+i/50*3+progress*3;const x=Math.cos(a)*r,y=Math.sin(a)*r;if(i===0)c.moveTo(x,y);else c.lineTo(x,y);}c.stroke();}
   } else if(e.pattern===4) {
    // The eight-spoked Dharma wheel: hub, spokes, and double rim.
    c.rotate(progress*2);
    for(const scale of [.14,.84,1]){c.beginPath();c.arc(0,0,radius*scale,0,Math.PI*2);c.stroke();}
    for(let i=0;i<8;i++){
     c.save();c.rotate(i*Math.PI/4);c.beginPath();c.moveTo(radius*.14,0);c.lineTo(radius*1.08,0);c.stroke();
     c.beginPath();c.arc(radius*1.08,0,3,0,Math.PI*2);c.fillStyle='#dec58b';c.fill();c.restore();
    }
   } else {
    // Two rotating mandala polygons and their halo.
    for(let layer=0;layer<2;layer++){c.save();c.rotate((layer?1:-1)*progress+layer*Math.PI/8);c.beginPath();for(let i=0;i<=8;i++){const a=i*Math.PI/4,r=radius*(layer?.72:1);if(i===0)c.moveTo(Math.cos(a)*r,Math.sin(a)*r);else c.lineTo(Math.cos(a)*r,Math.sin(a)*r);}c.stroke();c.restore();}
    c.beginPath();c.arc(0,0,radius,0,Math.PI*2);c.stroke();
   }
   c.restore();
  }
 }
 private draw(time:number) {
  const dt=Math.min((time-this.last)/1000,.05);this.last=time;
  const c=this.ctx,w=this.width,h=this.height,t=this.reduced?0:time/1000;
  c.clearRect(0,0,w,h);
  c.save();c.translate(w/2,h*.43);c.rotate(t*.018);
  const beat=this.reduced?0:Math.pow((Math.cos(t*Math.PI*2*108/60)+1)/2,8);
  for(let layer=0;layer<3;layer++) {
   c.strokeStyle=`rgba(180,160,104,${.08+beat*.025})`;c.lineWidth=.7;
   const radius=95+layer*52;c.beginPath();c.arc(0,0,radius,0,Math.PI*2);c.stroke();
   for(let i=0;i<12;i++){c.save();c.rotate(i*Math.PI/6+(layer%2? -t*.026:t*.01));c.beginPath();c.ellipse(0,-radius*.62,radius*.25,radius*.62,0,0,Math.PI*2);c.stroke();c.restore();}
  }
  c.restore();
  for(let i=0;i<38;i++){const x=(Math.sin(i*127.1)*.5+.5)*w,y=((i*97.3+t*(3+i%3))%h);c.fillStyle=`rgba(180,213,200,${.12+.1*Math.sin(t+i)})`;c.fillRect(x,y,1,1);}
  this.rings=this.rings.filter(r=>r.age<1.3);
  for(const r of this.rings){r.age+=dt;c.strokeStyle=`rgba(107,235,209,${(1-r.age/1.3)*.65})`;c.lineWidth=1;c.beginPath();c.arc(r.x,r.y,8+r.age*(r.big?270:95),0,Math.PI*2);c.stroke();}
  this.drawCelebrations(dt);
  this.particles=this.particles.filter(p=>p.life>0);
  for(const p of this.particles){p.life-=dt;p.x+=p.vx*dt;p.y+=p.vy*dt;p.vx*=.98;p.vy*=.98;c.fillStyle=`rgba(224,203,139,${Math.max(0,p.life/p.max)})`;c.fillRect(p.x,p.y,2,2);}
  requestAnimationFrame(t=>this.draw(t));
 }
}
